import { Controller } from "@hotwired/stimulus";
import SearchSerializer from "../utilities/search_serializer";
import FavoritesService from "../utilities/favorites_service";

export default class extends Controller {
  static values = { resultsUrl: String, filterData: Object };
  static targets = [
    'results', 'loading', 'keyword', 'clearKeyword',
    'languageSelect', 'categoriesContainer', 'vendorSelect',
    'cipServicesContainer', 'productTypesContainer', 'deviceTypesContainer',
    'docYearsContainer', 'productDisplayContainer',
    'sortProductIcon', 'sortVendorIcon'
  ];

  connect() {
    var self = this;
    this.defaults = this.filterDataValue;
    this.debounceTimer = null;

    this.serializer = new SearchSerializer({
      arrayKeys: {
        vendors: Number,
        productTypes: Number,
        deviceTypes: Number,
        docYears: Number,
        categories: Number,
        services: String,
        productDisplay: String
      }
    });

    this.searchDefaults = {
      vendors:        this._pluck(this.defaults.members, 'id'),
      productTypes:   this._pluck(this.defaults.productTypes, 'id'),
      deviceTypes:    this._pluck(this.defaults.deviceTypes, 'id'),
      docYears:       this._pluck(this.defaults.docYears, 'value'),
      categories:     this._pluck(this.defaults.categories, 'id'),
      services:       [],
      page:           1,
      lang:           'en',
      view:           'search',
      productDisplay: ['LISTING', 'DOC']
    };

    this.availableOptions = Object.assign({}, JSON.parse(JSON.stringify(this.searchDefaults)), {
      services:       this._pluck(this.defaults.cipServices, 'id'),
      productDisplay: this._pluck(this.defaults.productDisplay, 'id')
    });

    var rawParams = this.serializer.readFromHash();
    this.search = Object.assign(
      {},
      JSON.parse(JSON.stringify(this.searchDefaults)),
      this.serializer.deserialize(rawParams, this.availableOptions)
    );

    this._renderCategories();
    this._renderVendors();
    this._renderCipServices();
    this._renderProductTypes();
    this._renderDeviceTypes();
    this._renderDocYears();
    this._renderProductDisplay();
    this._renderLanguages();

    if (this.hasKeywordTarget && this.search.q) {
      this.keywordTarget.value = this.search.q;
    }

    this.element.querySelectorAll('[data-cloak]').forEach(function(el) {
      el.removeAttribute('data-cloak');
      el.style.display = '';
    });

    this._syncHashAndFetch();
  }

  keywordInput(event) {
    var self = this;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(function() {
      self.search.q = event.target.value || '';
      self.search.page = 1;
      self._syncHashAndFetch();
    }, 400);
  }

  clearKeyword() {
    this.search.q = '';
    if (this.hasKeywordTarget) this.keywordTarget.value = '';
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  setPage(event) {
    event.preventDefault();
    var page = event.currentTarget.dataset.page;
    if (page) {
      this.search.page = parseInt(page, 10);
      this._syncHashAndFetch();
      var wrapper = this.element.querySelector('.products-table-wrapper');
      if (wrapper) wrapper.scrollTop = 250;
    }
  }

  changeLanguage(event) {
    this.search.lang = event.target.value;
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  resetFilters(event) {
    if (event) event.preventDefault();
    this.search = JSON.parse(JSON.stringify(this.searchDefaults));
    if (this.hasKeywordTarget) this.keywordTarget.value = '';
    this._recheckAllFilters();
    this._syncHashAndFetch();
  }

  setCategory(event) {
    var categoryId = parseInt(event.currentTarget.dataset.categoryId, 10);
    var children = this.defaults.categories
      .filter(function(c) { return c.parent === categoryId; })
      .map(function(c) { return c.id; });
    this.search = JSON.parse(JSON.stringify(this.searchDefaults));
    this.search.categories = children.concat(categoryId);
    this._recheckAllFilters();
    this._syncHashAndFetch();
  }

  toggleSort(event) {
    var field = event.currentTarget.dataset.sortField;
    if (this.search.order !== field) {
      this.search.order = field;
      this.search.dir = 'asc';
    } else {
      this.search.dir = this.search.dir === 'desc' ? 'asc' : 'desc';
    }
    this.search.page = 1;
    this._updateSortIcons();
    this._syncHashAndFetch();
  }

  selectAllCategories() {
    this.search.categories = this._pluck(this.defaults.categories, 'id');
    this._recheckCategories();
    this._syncHashAndFetch();
  }

  deselectAllCategories() {
    this.search.categories = [];
    this._recheckCategories();
    this._syncHashAndFetch();
  }

  onCategoryChange(event) {
    var id = parseInt(event.target.value, 10);
    var checked = event.target.checked;
    var self = this;
    var childIds = this._getCategoryChildren(id);

    if (checked) {
      var toAdd = [id].concat(childIds);
      toAdd.forEach(function(cid) {
        if (self.search.categories.indexOf(cid) === -1) {
          self.search.categories.push(cid);
        }
      });
    } else {
      var toRemove = [id].concat(childIds);
      this.search.categories = this.search.categories.filter(function(cid) {
        return toRemove.indexOf(cid) === -1;
      });
    }

    this._recheckCategories();
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  selectAllProfileTypes() {
    this.search.deviceTypes = this._pluck(this.defaults.deviceTypes, 'id');
    this._recheckDeviceTypes();
    this._syncHashAndFetch();
  }

  deselectAllProfileTypes() {
    this.search.deviceTypes = [];
    this._recheckDeviceTypes();
    this._syncHashAndFetch();
  }

  resetVendors() {
    this.search.vendors = [];
    this._recheckVendors();
    this._syncHashAndFetch();
  }

  onCheckboxFilterChange(event) {
    var filterKey = event.target.dataset.filterKey;
    var rawVal = event.target.value;
    var isNumeric = ['vendors', 'productTypes', 'deviceTypes', 'docYears', 'categories'].indexOf(filterKey) !== -1;
    var val = isNumeric ? parseInt(rawVal, 10) : rawVal;
    var arr = this.search[filterKey] || [];

    if (event.target.checked) {
      if (arr.indexOf(val) === -1) arr.push(val);
    } else {
      arr = arr.filter(function(v) { return v !== val; });
    }
    this.search[filterKey] = arr;
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  onVendorChange(event) {
    var select = event.target;
    var selected = [];
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].selected) {
        selected.push(parseInt(select.options[i].value, 10));
      }
    }
    this.search.vendors = selected;
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  _applyCipBackgrounds() {
    var services = this.search.services || [];
    var bg = document.querySelector('.background-target');
    if (!bg) return;

    bg.classList.remove('cip-safety', 'cip-motion', 'cip-sync', 'cip-energy');
    var heading = document.querySelector('.ingredient-heading');

    var checks = {
      safety: services.indexOf('safety') !== -1,
      motion: services.indexOf('motion') !== -1,
      sync:   services.indexOf('sync') !== -1,
      energy: services.indexOf('energy') !== -1
    };
    var count = Object.values(checks).filter(Boolean).length;

    if (count === 1) {
      if (checks.safety) bg.classList.add('cip-safety');
      else if (checks.motion) bg.classList.add('cip-motion');
      else if (checks.sync) bg.classList.add('cip-sync');
      else if (checks.energy) bg.classList.add('cip-energy');

      if (heading) heading.style.display = '';
      ['safety', 'motion', 'sync', 'energy'].forEach(function(s) {
        var els = document.querySelectorAll('.cip-' + s + '-listing-text');
        els.forEach(function(el) { el.style.display = checks[s] ? '' : 'none'; });
      });
    }
  }

  _syncHashAndFetch() {
    var serialized = this.serializer.serialize(this.search, this.availableOptions);
    this.serializer.writeToHash(serialized);

    this._showLoading(true);
    this._applyCipBackgrounds();

    var params = window.location.hash.replace(/^#\??/, '');

    if (this.search.view === 'favorites') {
      var favs = FavoritesService.getAll();
      var ids = favs.length ? favs : [0];
      params += '&' + ids.map(function(id) { return 'ids=' + id; }).join('&');
    }

    var url = this.resultsUrlValue + '?' + params;

    var self = this;
    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function(response) { return response.text(); })
      .then(function(html) {
        if (self.hasResultsTarget) {
          self.resultsTarget.innerHTML = html;
          self.resultsTarget.style.display = '';
        }
        self._showLoading(false);
        self._updateSortIcons();
      })
      .catch(function(err) {
        console.error('Results fetch failed:', err);
        self._showLoading(false);
      });
  }

  _showLoading(show) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = show ? '' : 'none';
    }
    if (this.hasResultsTarget) {
      this.resultsTarget.style.display = show ? 'none' : '';
    }
  }

  _updateSortIcons() {
    var self = this;
    ['sortProductIcon', 'sortVendorIcon'].forEach(function(targetName) {
      var targetKey = targetName + 'Target';
      var hasKey = 'has' + targetName.charAt(0).toUpperCase() + targetName.slice(1) + 'Target';
      if (!self[hasKey]) return;
      var el = self[targetKey];
      var field = el.dataset.sortField;
      el.className = 'fa ' + (self.search.order === field
        ? (self.search.dir === 'desc' ? 'fa-sort-desc' : 'fa-sort-asc')
        : 'fa-sort');
    });
  }

  _renderCategories() {
    if (!this.hasCategoriesContainerTarget) return;
    var self = this;
    var html = this.defaults.categories.map(function(cat) {
      var checked = self.search.categories.indexOf(cat.id) !== -1 ? 'checked' : '';
      return '<div class="checkbox-line-dark category-level-' + (cat.level || 0) + '">' +
        '<input type="checkbox" id="category-' + cat.id + '" value="' + cat.id + '" ' +
        'data-action="change->product-search#onCategoryChange" ' + checked + '>' +
        '<label for="category-' + cat.id + '">' + cat.name + ' <i class="fa fa-angle-right"></i></label></div>';
    }).join('');
    this.categoriesContainerTarget.innerHTML = html;
  }

  _renderVendors() {
    if (!this.hasVendorSelectTarget) return;
    var self = this;
    var html = this.defaults.members.map(function(m) {
      var selected = self.search.vendors.indexOf(m.id) !== -1 ? 'selected' : '';
      return '<option value="' + m.id + '" ' + selected + '>' + m.name + '</option>';
    }).join('');
    this.vendorSelectTarget.innerHTML = html;

    if (typeof jQuery !== 'undefined' && jQuery.fn.multiselect) {
      jQuery(this.vendorSelectTarget).multiselect({
        buttonClass: 'btn btn-default',
        buttonWidth: '100%',
        maxHeight: 400,
        enableCaseInsensitiveFiltering: true,
        numberDisplayed: 2,
        includeSelectAllOption: true,
        selectAllText: 'Select/Deselect All',
        onChange: function() { self._onVendorMultiselectChange(); },
        onSelectAll: function() { self._onVendorMultiselectChange(); },
        onDeselectAll: function() { self._onVendorMultiselectChange(); }
      });
    }
  }

  _onVendorMultiselectChange() {
    var select = this.vendorSelectTarget;
    var selected = [];
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].selected) {
        selected.push(parseInt(select.options[i].value, 10));
      }
    }
    this.search.vendors = selected;
    this.search.page = 1;
    this._syncHashAndFetch();
  }

  _renderCheckboxList(container, items, idPrefix, filterKey, valueField, labelField) {
    if (!container) return;
    var self = this;
    var currentValues = this.search[filterKey] || [];
    var html = items.map(function(item) {
      var val = item[valueField];
      var label = item[labelField];
      var checked = currentValues.indexOf(val) !== -1 ? 'checked' : '';
      return '<li><div class="checkbox-line-dark">' +
        '<input type="checkbox" id="' + idPrefix + '-' + val + '" value="' + val + '" ' +
        'data-filter-key="' + filterKey + '" ' +
        'data-action="change->product-search#onCheckboxFilterChange" ' + checked + '>' +
        '<label for="' + idPrefix + '-' + val + '">' + label + '</label></div></li>';
    }).join('');
    container.innerHTML = html;
  }

  _renderCipServices() {
    if (!this.hasCipServicesContainerTarget) return;
    this._renderCheckboxList(
      this.cipServicesContainerTarget, this.defaults.cipServices,
      'cip', 'services', 'id', 'label'
    );
  }

  _renderProductTypes() {
    if (!this.hasProductTypesContainerTarget) return;
    this._renderCheckboxList(
      this.productTypesContainerTarget, this.defaults.productTypes,
      'product-type', 'productTypes', 'id', 'name'
    );
  }

  _renderDeviceTypes() {
    if (!this.hasDeviceTypesContainerTarget) return;
    this._renderCheckboxList(
      this.deviceTypesContainerTarget, this.defaults.deviceTypes,
      'device-type', 'deviceTypes', 'id', 'name'
    );
  }

  _renderDocYears() {
    if (!this.hasDocYearsContainerTarget) return;
    this._renderCheckboxList(
      this.docYearsContainerTarget, this.defaults.docYears,
      'doc-year', 'docYears', 'value', 'label'
    );
  }

  _renderProductDisplay() {
    if (!this.hasProductDisplayContainerTarget) return;
    this._renderCheckboxList(
      this.productDisplayContainerTarget, this.defaults.productDisplay,
      'product-display', 'productDisplay', 'id', 'label'
    );
  }

  _renderLanguages() {
    if (!this.hasLanguageSelectTarget) return;
    var self = this;
    var html = this.defaults.languages.map(function(lang) {
      var selected = self.search.lang === lang.id ? 'selected' : '';
      return '<option value="' + lang.id + '" ' + selected + '>' + lang.label + '</option>';
    }).join('');
    this.languageSelectTarget.innerHTML = html;
  }

  _recheckAllFilters() {
    this._recheckCategories();
    this._recheckVendors();
    this._recheckGenericFilter('cipServicesContainer', 'services');
    this._recheckGenericFilter('productTypesContainer', 'productTypes');
    this._recheckGenericFilter('deviceTypesContainer', 'deviceTypes');
    this._recheckGenericFilter('docYearsContainer', 'docYears');
    this._recheckGenericFilter('productDisplayContainer', 'productDisplay');
    if (this.hasKeywordTarget) this.keywordTarget.value = this.search.q || '';
    if (this.hasLanguageSelectTarget) this.languageSelectTarget.value = this.search.lang || 'en';
  }

  _recheckCategories() {
    if (!this.hasCategoriesContainerTarget) return;
    var self = this;
    this.categoriesContainerTarget.querySelectorAll('input[type=checkbox]').forEach(function(cb) {
      cb.checked = self.search.categories.indexOf(parseInt(cb.value, 10)) !== -1;
    });
  }

  _recheckVendors() {
    if (!this.hasVendorSelectTarget) return;
    var self = this;
    var select = this.vendorSelectTarget;
    for (var i = 0; i < select.options.length; i++) {
      select.options[i].selected = self.search.vendors.indexOf(parseInt(select.options[i].value, 10)) !== -1;
    }
    if (typeof jQuery !== 'undefined' && jQuery.fn.multiselect) {
      jQuery(select).multiselect('refresh');
    }
  }

  _recheckDeviceTypes() {
    this._recheckGenericFilter('deviceTypesContainer', 'deviceTypes');
  }

  _recheckGenericFilter(containerTargetName, filterKey) {
    var hasTarget = 'has' + containerTargetName.charAt(0).toUpperCase() + containerTargetName.slice(1) + 'Target';
    if (!this[hasTarget]) return;
    var container = this[containerTargetName + 'Target'];
    var vals = this.search[filterKey] || [];
    var isNumeric = ['vendors', 'productTypes', 'deviceTypes', 'docYears', 'categories'].indexOf(filterKey) !== -1;
    container.querySelectorAll('input[type=checkbox]').forEach(function(cb) {
      var cbVal = isNumeric ? parseInt(cb.value, 10) : cb.value;
      cb.checked = vals.indexOf(cbVal) !== -1;
    });
  }

  _pluck(arr, key) {
    if (!arr) return [];
    return arr.map(function(item) { return item[key]; });
  }

  _getCategoryChildren(parentId) {
    var self = this;
    var children = this.defaults.categories.filter(function(c) { return c.parent === parentId; });
    var result = [];
    children.forEach(function(child) {
      result.push(child.id);
      result = result.concat(self._getCategoryChildren(child.id));
    });
    return result;
  }
}
